import React, { useState, useEffect } from "react";
import { CircularProgress } from "@mui/material";
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  TablePagination, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Button, Divider,
  useMediaQuery // Import useMediaQuery for responsiveness
} from "@mui/material";
import { CheckCircle, Cancel } from "@mui/icons-material";
import baseURL from "../../config"; // Ensure this path is correct
// import SnackbarAlert from "../../Reusbale/SnackbarAlert"; // Assuming this is a custom component
import { toast } from "react-toastify"; // Using react-toastify for notifications

// Custom COLORS for a Microsoft-like aesthetic
const MS_COLORS = {
  primary: '#2D4A3D',       // Microsoft's signature blue
  primaryLight: '#EBF5EE',  // Lighter shade for active background
  text: '#323130',          // Dark gray for main text
  textSecondary: '#605D5C', // Lighter gray for secondary text
  background: '#FFFFFF',    // Pure white background
  backgroundLight: '#F3F2F1', // Light gray for subtle accents
  border: '#E1E1E1',        // Light gray for borders
  hoverBackground: '#F0F0F0', // Very light gray on hover
  white: '#FFFFFF',
  accent: '#107C10',        // A green for success actions
  red: '#D83B01',           // Microsoft's red for errors/rejection
};

const WithdrawalRequests = ({ withdrawals: initialWithdrawals }) => {
  const [withdrawals, setWithdrawals] = useState(initialWithdrawals || []);
  const [loadingInitial] = useState(false); // No initial fetch, so set to false
  const [processingAction, setProcessingAction] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [openDialog, setOpenDialog] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [selectedRequestId, setSelectedRequestId] = useState(null);

  const isSmallScreen = useMediaQuery("(max-width:600px)");

  // Sync state with the prop whenever it changes
  useEffect(() => {
    if (initialWithdrawals) {
      setWithdrawals(initialWithdrawals);
    }
  }, [initialWithdrawals]);

  // Handle page change
  const handleChangePage = (event, newPage) => setPage(newPage);

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle Approve request
  const approveRequest = async (id) => {
    setProcessingAction(true);
    try {
      const response = await fetch(`${baseURL}/admin/approve-withdrawal/${id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to approve withdrawal request");
      }

      setWithdrawals((prev) => prev.filter((request) => request.id !== id));
      toast.success("Withdrawal request approved successfully!");
    } catch (error) {
      console.error("Error approving withdrawal request:", error);
      toast.error(error.message || "Failed to approve withdrawal request.");
    } finally {
      setProcessingAction(false);
    }
  };

  // Open dialog for rejection
  const handleRejectClick = (id) => {
    setSelectedRequestId(id);
    setRejectionReason("");
    setOpenDialog(true);
  };

  // Handle rejection and send reason to the backend
  const handleRejectSubmit = async () => {
    if (!rejectionReason.trim()) {
      toast.error("Please provide a reason for rejection.");
      return;
    }

    setProcessingAction(true);
    try {
      const response = await fetch(`${baseURL}/admin/reject-withdrawal/${selectedRequestId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason: rejectionReason }),
        credentials: "include",
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to reject withdrawal request");
      }

      setWithdrawals((prev) => prev.filter((request) => request.id !== selectedRequestId));
      toast.info("Withdrawal request rejected.");
      setRejectionReason("");
      setOpenDialog(false);
    } catch (error) {
      console.error("Error rejecting withdrawal request:", error);
      toast.error(error.message || "Failed to reject withdrawal request.");
    } finally {
      setProcessingAction(false);
    }
  };

  // Handle rejection dialog close
  const handleDialogClose = () => {
    setOpenDialog(false);
    setRejectionReason("");
    setSelectedRequestId(null);
  };

  return (
    <Box
      sx={{
        backgroundColor: "#fff",
        borderRadius: "16px",
        boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
        border: "1px solid rgba(0,0,0,0.05)",
        p: 3,
        mt: 3,
      }}
    >
      <Typography
        variant="h6"
        sx={{
          color: "#1a1a2e",
          mb: 3,
          fontWeight: 800,
          fontSize: isSmallScreen ? "1.05rem" : "1.2rem",
          letterSpacing: "-0.3px",
        }}
      >
        Withdrawal Requests
      </Typography>

      {loadingInitial ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "200px" }}>
          <CircularProgress size={30} sx={{ color: MS_COLORS.primary }} />
          <Typography variant="body1" sx={{ ml: 2, color: MS_COLORS.textSecondary }}>Loading withdrawal requests...</Typography>
        </Box>
      ) : withdrawals.length === 0 ? (
        <Box
          sx={{
            textAlign: "center",
            mt: 3,
            p: 3,
            border: `1px dashed ${MS_COLORS.border}`,
            borderRadius: 1,
            bgcolor: MS_COLORS.backgroundLight,
          }}
        >
          <Typography sx={{ color: MS_COLORS.textSecondary, fontSize: "0.9rem" }}>
            No pending withdrawal requests available.
          </Typography>
        </Box>
      ) : (
        <>
          <TableContainer
            component={Paper}
            sx={{
              borderRadius: 1,
              boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
              overflowX: "auto",
              border: `1px solid ${MS_COLORS.border}`,
            }}
          >
            <Table sx={{ minWidth: 1000 }}>
              <TableHead sx={{ bgcolor: MS_COLORS.backgroundLight }}>
                <TableRow>
                  {[
                    "User/Organization Name", "Balance (UGX)", "Withdraw Amount (UGX)",
                    "System Charge (UGX) (4.25%)", "Withdraw Charge (UGX)", "Net Amount (UGX)",
                    "Phone Number", "Date of Request", "Actions"
                  ].map((header) => (
                    <TableCell key={header} sx={{ fontWeight: "bold", fontSize: "0.85rem", whiteSpace: "nowrap", color: MS_COLORS.text }}>
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {withdrawals.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((request) => (
                  <TableRow
                    key={request.id}
                    sx={{
                      "&:last-child td, &:last-child th": { border: 0 },
                      "&:hover": {
                        backgroundColor: MS_COLORS.hoverBackground,
                      },
                    }}
                  >
                    <TableCell sx={{ fontSize: "0.8rem", color: MS_COLORS.textSecondary, whiteSpace: "nowrap" }}>{request.owner_name}</TableCell>
                    <TableCell sx={{ fontSize: "0.8rem", color: MS_COLORS.textSecondary, whiteSpace: "nowrap" }}>UGX {request.balance.toLocaleString()}</TableCell>
                    <TableCell sx={{ fontSize: "0.8rem", color: request.amount > request.balance ? MS_COLORS.red : MS_COLORS.textSecondary, whiteSpace: "nowrap" }}>
                      UGX {request.amount.toLocaleString()}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.8rem", py: 1, color: MS_COLORS.textSecondary, whiteSpace: "nowrap" }}>UGX {request.system_charge.toLocaleString()}</TableCell>
                    <TableCell sx={{ fontSize: "0.8rem", py: 1, color: MS_COLORS.textSecondary, whiteSpace: "nowrap" }}>UGX {request.withdraw_charge.toLocaleString()}</TableCell>
                    <TableCell sx={{ fontSize: "0.8rem", py: 1, color: MS_COLORS.textSecondary, whiteSpace: "nowrap" }}>UGX {request.net_amount.toLocaleString()}</TableCell>
                    <TableCell sx={{ fontSize: "0.8rem", py: 1, color: MS_COLORS.textSecondary, whiteSpace: "nowrap" }}>{request.phone_number}</TableCell>
                    <TableCell sx={{ fontSize: "0.8rem", py: 1, color: MS_COLORS.textSecondary, whiteSpace: "nowrap" }}>{new Date(request.requested_at).toLocaleDateString("en-GB")}</TableCell>
                    <TableCell sx={{ display: "flex", gap: 1, alignItems: "center", py: 1, whiteSpace: "nowrap" }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => approveRequest(request.id)}
                        disabled={processingAction}
                        sx={{
                          textTransform: "none",
                          fontSize: "0.75rem",
                          borderRadius: 1,
                          borderColor: MS_COLORS.accent,
                          color: MS_COLORS.accent,
                          "&:hover": {
                            backgroundColor: MS_COLORS.accent + '10',
                            borderColor: MS_COLORS.accent,
                          },
                          "&:disabled": {
                            borderColor: MS_COLORS.border,
                            color: MS_COLORS.textSecondary,
                          },
                        }}
                      >
                        {processingAction && selectedRequestId === request.id ? (
                          <CircularProgress size={16} sx={{ mr: 0.5, color: MS_COLORS.accent }} />
                        ) : (
                          <CheckCircle sx={{ mr: 0.5, fontSize: "small" }} />
                        )}
                        Approve
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        color="error"
                        onClick={() => handleRejectClick(request.id)}
                        disabled={processingAction}
                        sx={{
                          textTransform: "none",
                          fontSize: "0.75rem",
                          borderRadius: 1,
                          borderColor: MS_COLORS.red,
                          color: MS_COLORS.red,
                          "&:hover": {
                            backgroundColor: MS_COLORS.red + '10',
                            borderColor: MS_COLORS.red,
                          },
                          "&:disabled": {
                            borderColor: MS_COLORS.border,
                            color: MS_COLORS.textSecondary,
                          },
                        }}
                      >
                        <Cancel sx={{ mr: 0.5, fontSize: "small" }} />
                        Reject
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={withdrawals.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select, & .MuiTablePagination-actions":
              {
                fontSize: "0.8rem",
                color: MS_COLORS.textSecondary,
              },
            }}
          />
        </>
      )}

      <Dialog
        open={openDialog}
        onClose={handleDialogClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: { borderRadius: "8px", p: 2, boxShadow: "0 4px 12px rgba(0,0,0,0.1)" },
        }}
      >
        <DialogTitle sx={{ fontWeight: "bold", fontSize: "1.2rem", color: MS_COLORS.text }}>
          Reject Withdrawal Request
        </DialogTitle>
        <Divider sx={{ mb: 2 }} />
        <DialogContent>
          <TextField
            label="Reason for Rejection"
            multiline
            rows={4}
            fullWidth
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            variant="outlined"
            margin="normal"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "4px",
                "& fieldset": { borderColor: MS_COLORS.border },
                "&:hover fieldset": { borderColor: MS_COLORS.primary },
                "&.Mui-focused fieldset": { borderColor: MS_COLORS.primary, borderWidth: 2 },
              },
              "& .MuiInputBase-input": { fontSize: "0.9rem", color: MS_COLORS.text },
              "& .MuiInputLabel-root": { fontSize: "0.9rem", color: MS_COLORS.textSecondary }
            }}
          />
        </DialogContent>
        <Divider sx={{ mt: 2 }} />
        <DialogActions sx={{ justifyContent: "flex-end", pt: 2, px: 3 }}>
          <Button
            onClick={handleDialogClose}
            variant="outlined"
            sx={{
              borderRadius: "4px",
              fontSize: "0.85rem",
              textTransform: "none",
              borderColor: MS_COLORS.border,
              color: MS_COLORS.text,
              "&:hover": { borderColor: MS_COLORS.primary, backgroundColor: MS_COLORS.primaryLight + '30' }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRejectSubmit}
            disabled={!rejectionReason.trim() || processingAction}
            variant="contained"
            sx={{
              textTransform: "none",
              borderRadius: "4px",
              backgroundColor: MS_COLORS.red,
              color: MS_COLORS.white,
              display: "flex",
              alignItems: "center",
              gap: 1,
              minWidth: 120,
              "&:hover": { backgroundColor: MS_COLORS.red, opacity: 0.9 },
              "&:disabled": {
                backgroundColor: MS_COLORS.border,
                color: MS_COLORS.textSecondary,
              }
            }}
          >
            {processingAction ? (
              <>
                <CircularProgress size={18} sx={{ color: MS_COLORS.white, mr: 1 }} />
                Rejecting...
              </>
            ) : (
              "Reject"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WithdrawalRequests;